import { useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { MIN_REFLECTION_CHARS } from '../../helpers/blockRules'
import Markdown from '../Markdown'
import { bodyText, colours } from '../player/playerStyles'
import useBlockState from './useBlockState'

const MAX_CHARS = 4000
const lengthOf = (value) => String(value ?? '').trim().length

// Free-text reflections. Typing is kept by the player as a draft (so it
// survives moving between screens and a failed save); pressing Save writes
// it to the training record, and it can be edited and saved again later.
const Reflection = ({ block, initialState, completed, onStateChange, onSave }) => {
  const [state, update] = useBlockState({ responses: {}, ...initialState }, onStateChange)
  // 'idle' | 'short' | 'saving' | 'saved' | 'failed'
  const [status, setStatus] = useState('idle')
  const responses = state.responses ?? {}

  const change = (promptId, value) => {
    update({ ...state, responses: { ...responses, [promptId]: value } })
    setStatus('idle')
  }

  const save = async () => {
    if (status === 'saving') return
    if (block.prompts.some((prompt) => lengthOf(responses[prompt.id]) < MIN_REFLECTION_CHARS)) {
      setStatus('short')
      return
    }
    setStatus('saving')
    const saved = await onSave?.({ responses })
    setStatus(saved ? 'saved' : 'failed')
  }

  return (
    <Box sx={{ maxWidth: '74ch' }}>
      {block.intro && <Markdown sx={{ ...bodyText, mb: 2 }}>{block.intro}</Markdown>}
      <Typography sx={{ ...bodyText, mb: 2.5 }}>
        Your reflections are saved to your training record and printed on your certificate.
      </Typography>

      {block.prompts.map((prompt) => {
        const length = lengthOf(responses[prompt.id])
        const tooShort = status === 'short' && length < MIN_REFLECTION_CHARS
        const fieldId = `${block.id}-${prompt.id}`
        return (
          <Box key={prompt.id} sx={{ mb: 3 }}>
            <Typography component="label" htmlFor={fieldId} sx={{ display: 'block', fontSize: '1.0625rem', fontWeight: 700, lineHeight: 1.5, mb: 1 }}>
              {prompt.text}
            </Typography>
            <TextField
              id={fieldId}
              multiline
              minRows={4}
              fullWidth
              value={responses[prompt.id] ?? ''}
              onChange={(event) => change(prompt.id, event.target.value)}
              error={tooShort}
              inputProps={{ maxLength: MAX_CHARS, 'aria-describedby': `${fieldId}-help` }}
              sx={{
                '& .MuiInputBase-root': { fontSize: '1.0625rem', lineHeight: 1.6, bgcolor: '#FFFFFF' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: colours.border },
              }}
            />
            <Typography id={`${fieldId}-help`} sx={{ fontSize: '1rem', mt: 0.5, color: tooShort ? colours.bad : colours.muted, fontWeight: tooShort ? 700 : 400 }}>
              {tooShort ? 'Too short: write' : 'Write'} at least {MIN_REFLECTION_CHARS} characters. You have written {length}.
            </Typography>
          </Box>
        )
      })}

      <Button variant="contained" onClick={save} aria-disabled={status === 'saving'}>
        {completed ? 'Save changes to your reflection' : 'Save reflection'}
      </Button>
      <Typography
        aria-live="polite"
        sx={{ fontSize: '1rem', fontWeight: 700, mt: 1, minHeight: '1.6em', color: status === 'short' || status === 'failed' ? colours.bad : colours.good }}
      >
        {status === 'short' && `Write at least ${MIN_REFLECTION_CHARS} characters for each prompt, then save.`}
        {status === 'saving' && 'Saving your reflection.'}
        {status === 'saved' && 'Reflection saved.'}
        {status === 'failed' && 'Your reflection was not saved. Your text is still here: try saving again.'}
      </Typography>
    </Box>
  )
}

export default Reflection
